import { isMiniprogram, isWechatBrowser } from '@lighthouse/tools'
import { Carousel } from '@mantine/carousel'
import { useGesture } from '@use-gesture/react'
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useImmer } from 'use-immer'

import { isMobileDevice } from '../../../utils'
import { FileControl } from '../FileControl'
import type { FileItemProps } from '../types'
import { shouldEnableWechatNativeImageMenu } from '../wechatImageLongPress'
import { AudioFile } from './Audio'
import { ImageFile } from './Image'
import { NotSupportFile } from './NotSupportFile'
import { OfficeFile } from './Office'
import { VideoFile } from './Video'

const PdfFile = React.lazy(() => import('./Pdf').then(module => ({ default: module.PdfFile })))

// 初始形状
const INIT_SCALE = 100
const INIT_ROTATE = 0
// 限制
const MAX_SCALE = 1500
const MIN_SCALE = 20
// 增量
const SCALE_STEP = 10
const ROTATE_STEP = 90

const ALLOW_SCALE = new Set(['image', 'doc', 'pdf', 'excel', 'ppt'])
const ALLOW_ROTATE = new Set(['image'])

interface Props extends FileItemProps {
    active: boolean
    disabledSlider?: boolean
}

export const FileItem = ({
    file,
    active,
    disabledSlider,
    prefix,
    showPdfThumbs,
    onlyPdfSinglePage,
    disableWechatDocumentPreview
}: Props) => {
    const [mounted, setMounted] = useState(false)

    const isMobile = isMobileDevice()
    const enableWechatNativeMenu = shouldEnableWechatNativeImageMenu(file, {
        isMobile,
        isWechatBrowser: isWechatBrowser(),
        isMiniprogram: isMiniprogram()
    })

    const [transform, setTransform] = useImmer({
        scale: INIT_SCALE,
        rotate: INIT_ROTATE
    })

    // 添加拖拽偏移状态，仅用于移动端手势交互
    const [dragOffset, setDragOffset] = useImmer({
        x: 0,
        y: 0
    })

    useEffect(() => {
        if (active) {
            setTransform({ scale: INIT_SCALE, rotate: INIT_ROTATE })
            setDragOffset({ x: 0, y: 0 }) // 重置拖拽偏移
            setMounted(true)
        }
    }, [active, setTransform, setDragOffset])

    const onZoomIn = useCallback(() => {
        setTransform(draft => {
            if (draft.scale <= MIN_SCALE) {
                return
            }
            draft.scale -= SCALE_STEP
        })
        // 电脑端缩放时重置拖拽偏移
        if (!isMobile) {
            setDragOffset({ x: 0, y: 0 })
        }
    }, [setTransform, setDragOffset, isMobile])

    const onZoomOut = useCallback(() => {
        setTransform(draft => {
            if (draft.scale >= MAX_SCALE) {
                return
            }
            draft.scale += SCALE_STEP
        })
        // 电脑端缩放时重置拖拽偏移
        if (!isMobile) {
            setDragOffset({ x: 0, y: 0 })
        }
    }, [setTransform, setDragOffset, isMobile])

    const onRotate = useCallback(() => {
        setTransform(draft => {
            draft.rotate = (draft.rotate + ROTATE_STEP) % 360
        })
        // 旋转时重置拖拽偏移
        setDragOffset({ x: 0, y: 0 })
    }, [setTransform, setDragOffset])

    const content = useMemo(() => {
        if (!mounted || !file?.url) {
            return null
        }
        switch (file.type) {
            case 'image': {
                return (
                    <ImageFile
                        file={file}
                        transform={transform}
                        dragOffset={dragOffset}
                        enableWechatNativeMenu={enableWechatNativeMenu}
                    />
                )
            }
            case 'video': {
                return <VideoFile file={file} onClick={ev => ev.stopPropagation()} />
            }
            case 'audio': {
                return <AudioFile file={file} onClick={ev => ev.stopPropagation()} />
            }
            case 'pdf': {
                return (
                    <Suspense fallback={<div>Loading...</div>}>
                        <PdfFile
                            onlyPdfSinglePage={onlyPdfSinglePage}
                            showPdfThumbs={showPdfThumbs}
                            file={file}
                            prefix={prefix}
                            transform={transform}
                            dragOffset={dragOffset}
                            disableIntersectionObserver={disabledSlider}
                        />
                    </Suspense>
                )
            }
            case 'xls':
            case 'xlsx':
            case 'doc':
            case 'docx':
            case 'ppt':
            case 'pptx': {
                return (
                    <OfficeFile
                        file={file}
                        transform={transform}
                        dragOffset={dragOffset}
                        disableWechatDocumentPreview={disableWechatDocumentPreview}
                    />
                )
            }
            // case 'xls':
            // case 'xlsx': {
            //     return <NotSupportFile name={file.name} icon="FileXsl" />
            // }
            // case 'doc':
            // case 'docx': {
            //     return <NotSupportFile name={file.name} icon="FileDoc" />
            // }
            // case 'ppt':
            // case 'pptx': {
            //     return <NotSupportFile name={file.name} icon="FilePpt" />
            // }
            case 'txt': {
                return <NotSupportFile name={file.name} icon="FileTxt" />
            }
            case 'zip': {
                return <NotSupportFile name={file.name} icon="FileZip" />
            }

            default: {
                return <NotSupportFile name={file.name} icon="FileNothing" />
            }
        }
    }, [
        disabledSlider,
        disableWechatDocumentPreview,
        file,
        mounted,
        prefix,
        onlyPdfSinglePage,
        showPdfThumbs,
        transform,
        dragOffset,
        enableWechatNativeMenu
    ])

    const ref = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (enableWechatNativeMenu) {
            return
        }
        const handler = (e: Event) => e.preventDefault()
        document.addEventListener('gesturestart', handler)
        document.addEventListener('gesturechange', handler)
        document.addEventListener('gestureend', handler)
        return () => {
            document.removeEventListener('gesturestart', handler)
            document.removeEventListener('gesturechange', handler)
            document.removeEventListener('gestureend', handler)
        }
    }, [enableWechatNativeMenu])

    // 统一的手势处理，实现智能的事件传播控制
    const bind = useGesture(
        {
            onPinch: ({ offset: [s], event }) => {
                // 捏合时总是阻止轮播
                event.stopPropagation()

                const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, s * 100))
                setTransform(draft => {
                    draft.scale = clampedScale
                })

                if (clampedScale <= 100) {
                    setDragOffset({ x: 0, y: 0 })
                }
            },
            onDrag: ({ cancel, offset: [x, y], event, tap, pinching }) => {
                if (tap || pinching) {
                    return cancel()
                }

                const currentScale = transform.scale / 100

                // 情况1: 图片未放大，直接让轮播处理所有拖拽
                if (transform.scale <= 100) {
                    // 不阻止事件传播，让 Carousel 自然处理
                    return cancel() // 取消我们的手势处理
                }

                // 情况2: 图片已放大，需要阻止轮播
                event.stopPropagation()

                const container = ref.current
                if (!container) {
                    return cancel()
                }

                const containerRect = container.getBoundingClientRect()
                const imageWidth = containerRect.width * currentScale
                const imageHeight = containerRect.height * currentScale

                // 计算拖拽边界
                const maxX = Math.max(0, (imageWidth - containerRect.width) / 2)
                const maxY = Math.max(0, (imageHeight - containerRect.height) / 2)

                // 限制拖拽范围
                const clampedX = Math.max(-maxX, Math.min(maxX, x))
                const clampedY = Math.max(-maxY, Math.min(maxY, y))

                setDragOffset({ x: clampedX, y: clampedY })
            }
        },
        {
            enabled: active && isMobile && !enableWechatNativeMenu,
            drag: {
                pointer: { touch: true },
                from: () => [dragOffset.x, dragOffset.y],
                eventOptions: { passive: false, capture: true }, // 在捕获阶段处理，优先级更高
                rubberband: true,
                filterTaps: true
            },
            pinch: {
                pointer: { touch: true },
                from: () => [transform.scale / 100, 0],
                eventOptions: { passive: false, capture: true }, // 在捕获阶段处理，优先级更高
                rubberband: true
                // threshold: 0.01
            }
        }
    )

    const contentEle = useMemo(() => {
        return (
            <div
                ref={ref}
                style={{ touchAction: enableWechatNativeMenu ? 'auto' : 'none', width: '100%', height: '100%' }}
                {...(enableWechatNativeMenu ? undefined : bind())}
            >
                {content}
                {isMobile ? null : (
                    <FileControl
                        zoomIn={ALLOW_SCALE.has(file.type) ? { handler: onZoomIn, disabled: transform.scale <= MIN_SCALE } : false}
                        zoomOut={ALLOW_SCALE.has(file.type) ? { handler: onZoomOut, disabled: transform.scale >= MAX_SCALE } : false}
                        rotate={ALLOW_ROTATE.has(file.type) ? { handler: onRotate } : false}
                        // download={{ handler: onDownload }}
                    />
                )}
            </div>
        )
    }, [bind, content, enableWechatNativeMenu, file.type, isMobile, onRotate, onZoomIn, onZoomOut, transform.scale])

    return disabledSlider ? contentEle : <Carousel.Slide>{contentEle}</Carousel.Slide>
}
